import { useEffect, useState } from 'react';
import i18n from 'i18next';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { catalogResources, isLocale, normalizeLocale, type Locale } from '@wikigold/i18n';
import { directionForLocale, localeFromPath, localePath } from './locale-routing';
import { isIntroductionPath, introLocaleFromSearch, localeSelectionHref } from './entry-routing';

function cookieLocale(): Locale | null {
  if (typeof document === 'undefined') return null;
  const value = document.cookie.split(';').map(part => part.trim()).find(part => part.startsWith('wg_locale='))?.slice('wg_locale='.length);
  return value ? normalizeLocale(value) : null;
}

function browserLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  return normalizeLocale(navigator.languages?.[0] ?? navigator.language);
}

export function initialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  return localeFromPath(window.location.pathname)
    ?? (isIntroductionPath(window.location.pathname) ? introLocaleFromSearch(window.location.search) : null)
    ?? cookieLocale() ?? browserLocale();
}

const locale = initialLocale();
void i18n.use(initReactI18next).init({
  resources: catalogResources(),
  lng: locale,
  fallbackLng: 'en',
  initAsync: false,
  interpolation: { escapeValue: false },
  returnNull: false,
});

function applyDocumentLocale(next: Locale) {
  document.documentElement.lang = next;
  document.documentElement.dir = directionForLocale(next);
}

applyDocumentLocale(locale);

function replaceLocalePath(next: Locale) {
  if (isIntroductionPath(location.pathname)) return;
  history.replaceState({}, '', `${localePath(next, location.pathname)}${location.search}${location.hash}`);
}

export function setAppLocale(next: Locale) {
  document.cookie = `wg_locale=${next}; Path=/; Max-Age=31536000; SameSite=Lax`;
  applyDocumentLocale(next);
  history.replaceState({}, '', localeSelectionHref(next, location.pathname, location.search, location.hash));
  void i18n.changeLanguage(next);
  if (isIntroductionPath(location.pathname)) return;
  void fetch('/api/gold-v2/locale', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', 'accept-language': next },
    body: JSON.stringify({ locale: next }),
    keepalive: true,
  }).catch(() => {});
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(() => Boolean(localeFromPath(location.pathname)
    || (isIntroductionPath(location.pathname) && introLocaleFromSearch(location.search)) || cookieLocale()));
  useEffect(() => {
    const synchronize = () => {
      const next = initialLocale();
      applyDocumentLocale(next);
      // Navigation restores the URL's language without changing its history or
      // treating a restored page as an explicit language preference update.
      if (i18n.resolvedLanguage !== next) void i18n.changeLanguage(next);
    };
    window.addEventListener('popstate', synchronize);
    window.addEventListener('pageshow', synchronize);
    return () => {
      window.removeEventListener('popstate', synchronize);
      window.removeEventListener('pageshow', synchronize);
    };
  }, []);
  useEffect(() => {
    if (ready) return;
    let active = true;
    void fetch('/api/gold-v2/locale-hint', {
      credentials: 'include',
      headers: { 'accept-language': browserLocale() },
      signal: AbortSignal.timeout(1800),
    })
      .then(response => response.ok ? response.json() as Promise<{ locale?: unknown }> : null)
      .then(async result => {
        if (!active) return;
        const next = typeof result?.locale === 'string' && isLocale(result.locale)
          ? result.locale
          : browserLocale();
        applyDocumentLocale(next);
        replaceLocalePath(next);
        await i18n.changeLanguage(next);
      })
      .catch(() => {})
      .finally(() => { if (active) setReady(true); });
    return () => { active = false; };
  }, [ready]);
  return <I18nextProvider i18n={i18n}>
    {ready ? children : <div className="gold-loading" aria-busy="true"><span>⛏</span></div>}
  </I18nextProvider>;
}

export { i18n };
